/**
 * Campaigns page. Backed by the Zustand campaign store. Supports:
 *  - filtering by ad account + status,
 *  - syncing campaigns from Facebook for the selected ad account,
 *  - toggling a campaign's status (ACTIVE/PAUSED),
 *  - duplicating a campaign (optional new name),
 *  - editing daily / lifetime budget.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import Card from '@/components/Cards/Card';
import Table, { type Column } from '@/components/Tables/Table';
import Button from '@/components/Forms/Button';
import Input from '@/components/Forms/Input';
import Select, { type SelectOption } from '@/components/Forms/Select';
import MultiSelect from '@/components/Forms/MultiSelect';
import Badge from '@/components/common/Badge';
import Alert from '@/components/Alerts/Alert';
import Modal from '@/components/Modals/Modal';
import { useToast } from '@/components/Alerts/toastContext';
import { useCampaignStore } from '@/store/campaignStore';
import * as adAccountService from '@/services/adAccountService';
import * as campaignService from '@/services/campaignService';
import type { AdAccountDTO, CampaignDTO } from '@shared/types';
import { adAccountNumber, adsManagerUrl, formatCurrency, formatNumber } from '@/utils/formatters';
import { RANGE_OPTIONS, presetToRange, type RangePreset } from '@/utils/dateRange';
import { entityStatusMeta } from '@/pages/labels';

/** Effectiveness badge derived from ROAS (green ≥ 1x, red < 1x, — if no data). */
function roasCell(row: CampaignDTO): ReactElement {
  if (row.insightsSyncedAt == null || row.roas == null) {
    return <span className="text-gray-400">—</span>;
  }
  const roas = Number(row.roas);
  if (!Number.isFinite(roas)) return <span className="text-gray-400">—</span>;
  const color = roas >= 1 ? 'green' : 'red';
  return <Badge color={color}>{roas.toFixed(2)}x</Badge>;
}

/** Real ROAS (website revenue / spend) badge. */
function realRoasCell(row: CampaignDTO): ReactElement {
  if (row.realRoas == null) return <span className="text-gray-400">—</span>;
  const r = Number(row.realRoas);
  if (!Number.isFinite(r)) return <span className="text-gray-400">—</span>;
  return <Badge color={r >= 1 ? 'green' : 'red'}>{r.toFixed(2)}x</Badge>;
}

/** Special (non-Facebook) status filter: campaigns actually spending in range. */
const SPENDING_FILTER = 'SPENDING';

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Đang hoạt động' },
  { value: SPENDING_FILTER, label: 'Đang chạy (có chi tiêu)' },
  { value: 'PAUSED', label: 'Tạm dừng' },
  { value: 'ARCHIVED', label: 'Đã lưu trữ' },
];

/** Campaigns management view. */
export default function Campaigns(): ReactElement {
  const toast = useToast();

  const campaigns = useCampaignStore((s) => s.campaigns);
  const loading = useCampaignStore((s) => s.loading);
  const error = useCampaignStore((s) => s.error);
  const fetch = useCampaignStore((s) => s.fetch);
  const sync = useCampaignStore((s) => s.sync);
  const copy = useCampaignStore((s) => s.copy);
  const updateStatus = useCampaignStore((s) => s.updateStatus);
  const updateBudget = useCampaignStore((s) => s.updateBudget);
  const totals = useCampaignStore((s) => s.totals);

  const [adAccounts, setAdAccounts] = useState<AdAccountDTO[]>([]);
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [status, setStatus] = useState('');
  const [rangePreset, setRangePreset] = useState<RangePreset>('last30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncingRevenue, setSyncingRevenue] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Copy modal state.
  const [copyTarget, setCopyTarget] = useState<CampaignDTO | null>(null);
  const [newName, setNewName] = useState('');
  const [copying, setCopying] = useState(false);

  // Budget modal state.
  const [budgetTarget, setBudgetTarget] = useState<CampaignDTO | null>(null);
  const [dailyBudget, setDailyBudget] = useState('');
  const [lifetimeBudget, setLifetimeBudget] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);

  // Mark/unmark an existing campaign as a reusable template (row action).
  const [markingId, setMarkingId] = useState<string | null>(null);

  // Bulk selection + actions.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkBudgetValue, setBulkBudgetValue] = useState('');
  const [bulking, setBulking] = useState(false);

  // Bulk rename modal — set a new base name + optional suffix + auto sequence.
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameBase, setRenameBase] = useState('');
  const [renameSuffix, setRenameSuffix] = useState('');
  const [renameNumber, setRenameNumber] = useState(true);
  const [renameStart, setRenameStart] = useState('1');
  const [renaming, setRenaming] = useState(false);

  useEffect(() => {
    adAccountService
      .list()
      .then(setAdAccounts)
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Không thể tải tài khoản quảng cáo');
      });
  }, [toast]);

  const loadCampaigns = useCallback(
    async (refresh = false): Promise<void> => {
      const range =
        rangePreset === 'custom'
          ? { from: customFrom || undefined, to: customTo || undefined }
          : presetToRange(rangePreset);
      // For a custom range, wait until both dates are chosen.
      if (rangePreset === 'custom' && (!customFrom || !customTo)) return;
      setSelectedIds([]);
      try {
        await fetch({
          adAccountIds: accountIds,
          // "Đang chạy" is a derived filter (spend > 0), not a Facebook status.
          status: status && status !== SPENDING_FILTER ? status : undefined,
          spending: status === SPENDING_FILTER,
          from: range.from,
          to: range.to,
          refresh,
        });
      } catch {
        // Error is captured in the store and rendered below.
      }
    },
    [fetch, accountIds, status, rangePreset, customFrom, customTo],
  );

  useEffect(() => {
    void loadCampaigns();
  }, [loadCampaigns]);

  const handleSync = async (): Promise<void> => {
    // Campaign sync pulls from one ad account at a time.
    if (accountIds.length !== 1) {
      toast.warning('Vui lòng chọn đúng 1 tài khoản quảng cáo để đồng bộ chiến dịch');
      return;
    }
    setSyncing(true);
    try {
      const result = await sync(accountIds[0]);
      toast.success(result.message ?? `Đã đồng bộ ${result.synced} chiến dịch`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Đồng bộ chiến dịch thất bại');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncRevenue = async (): Promise<void> => {
    setSyncingRevenue(true);
    try {
      const result = await campaignService.syncSheetRevenue();
      toast.success(result.message);
      if (result.unmatchedRoutes.length > 0) {
        const top = result.unmatchedRoutes
          .slice(0, 5)
          .map((u) => `${u.route} (${Math.round(u.amount).toLocaleString('vi-VN')}đ)`)
          .join(', ');
        toast.warning(`Route chưa khớp campaign: ${top}${result.unmatchedRoutes.length > 5 ? '…' : ''}`);
      }
      await loadCampaigns(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Đồng bộ doanh thu thất bại');
    } finally {
      setSyncingRevenue(false);
    }
  };

  // ONE button to refresh everything (revenue + accounts + campaigns + insights),
  // then hard-reload so every number on screen is fresh (no stale illusion).
  const handleRefreshAll = async (): Promise<void> => {
    setRefreshing(true);
    try {
      const r = await campaignService.refreshAll();
      toast.success(r.message || 'Đã làm mới tất cả');
      window.location.reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Làm mới thất bại');
      setRefreshing(false);
    }
  };

  const handleToggleStatus = async (campaign: CampaignDTO): Promise<void> => {
    const next = (campaign.status ?? '').toUpperCase() === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setBusyId(campaign.id);
    try {
      await updateStatus(campaign.id, next);
      toast.success(next === 'ACTIVE' ? 'Đã kích hoạt chiến dịch' : 'Đã tạm dừng chiến dịch');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Cập nhật trạng thái thất bại');
    } finally {
      setBusyId(null);
    }
  };

  const openCopy = (campaign: CampaignDTO): void => {
    setCopyTarget(campaign);
    setNewName(campaign.name ? `${campaign.name} (Bản sao)` : '');
  };

  const handleCopy = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!copyTarget) return;
    setCopying(true);
    try {
      await copy(copyTarget.id, { newCampaignName: newName.trim() || undefined });
      toast.success('Đã sao chép chiến dịch');
      setCopyTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sao chép chiến dịch thất bại');
    } finally {
      setCopying(false);
    }
  };

  const openBudget = (campaign: CampaignDTO): void => {
    setBudgetTarget(campaign);
    setDailyBudget(campaign.dailyBudget ?? '');
    setLifetimeBudget(campaign.lifetimeBudget ?? '');
  };

  const handleSaveBudget = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!budgetTarget) return;
    const daily = dailyBudget.trim() === '' ? undefined : Number(dailyBudget);
    const lifetime = lifetimeBudget.trim() === '' ? undefined : Number(lifetimeBudget);
    if (daily === undefined && lifetime === undefined) {
      toast.warning('Vui lòng nhập ít nhất một loại ngân sách');
      return;
    }
    if ((daily !== undefined && !Number.isFinite(daily)) || (lifetime !== undefined && !Number.isFinite(lifetime))) {
      toast.warning('Ngân sách không hợp lệ');
      return;
    }
    setSavingBudget(true);
    try {
      await updateBudget(budgetTarget.id, { dailyBudget: daily, lifetimeBudget: lifetime });
      toast.success('Đã cập nhật ngân sách');
      setBudgetTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Cập nhật ngân sách thất bại');
    } finally {
      setSavingBudget(false);
    }
  };

  const handleToggleTemplate = async (campaign: CampaignDTO): Promise<void> => {
    setMarkingId(campaign.id);
    try {
      await campaignService.setTemplate(campaign.id, !campaign.isTemplate);
      toast.success(campaign.isTemplate ? 'Đã bỏ đánh dấu mẫu' : 'Đã đánh dấu làm mẫu');
      await loadCampaigns(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Cập nhật mẫu thất bại');
    } finally {
      setMarkingId(null);
    }
  };

  const handleBulkStatus = async (next: 'ACTIVE' | 'PAUSED'): Promise<void> => {
    if (selectedIds.length === 0) return;
    setBulking(true);
    try {
      const r = await campaignService.bulkStatus(selectedIds, next);
      toast.success(r.message);
      const failed = r.results.filter((x) => !x.ok);
      if (failed.length > 0) {
        toast.error(
          `${failed.length} camp không sửa được — Facebook chặn camp đặc biệt (boosted post / Advantage+ / ngân sách ở cấp nhóm QC).`,
        );
      }
      await loadCampaigns(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Thao tác hàng loạt thất bại');
    } finally {
      setBulking(false);
    }
  };

  const handleBulkBudget = async (): Promise<void> => {
    if (selectedIds.length === 0) return;
    const b = Number(bulkBudgetValue);
    if (!Number.isFinite(b) || b <= 0) {
      toast.warning('Ngân sách/ngày không hợp lệ');
      return;
    }
    setBulking(true);
    try {
      const r = await campaignService.bulkBudget(selectedIds, b);
      toast.success(r.message);
      const failed = r.results.filter((x) => !x.ok);
      if (failed.length > 0) {
        toast.error(
          `${failed.length} camp không sửa được — Facebook chặn camp đặc biệt (boosted post / Advantage+ / ngân sách ở cấp nhóm QC).`,
        );
      }
      setBulkBudgetValue('');
      await loadCampaigns(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Cập nhật ngân sách hàng loạt thất bại');
    } finally {
      setBulking(false);
    }
  };

  // Build the new name for the i-th selected campaign: base + suffix + sequence.
  const buildRenamed = (i: number): string => {
    let name = renameBase.trim();
    if (renameSuffix.trim()) name += ` ${renameSuffix.trim()}`;
    if (renameNumber) name += ` ${(Number(renameStart) || 1) + i}`;
    return name.trim();
  };

  const handleBulkRename = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (selectedIds.length === 0) return;
    if (!renameBase.trim()) {
      toast.warning('Vui lòng nhập tên mới');
      return;
    }
    const sel = new Set(selectedIds);
    const selected = campaigns.filter((c) => sel.has(c.id)); // visible order
    const items = selected.map((c, i) => ({ id: c.id, name: buildRenamed(i) })).filter((it) => it.name.length > 0);
    if (items.length === 0) {
      toast.warning('Không tạo được tên hợp lệ');
      return;
    }
    setRenaming(true);
    try {
      const r = await campaignService.bulkRename(items);
      toast.success(r.message);
      const failed = r.results.filter((x) => !x.ok);
      if (failed.length > 0) {
        toast.error(`${failed.length} camp không đổi được tên — Facebook chặn camp đặc biệt.`);
      }
      setRenameOpen(false);
      setRenameBase('');
      setRenameSuffix('');
      setRenameNumber(true);
      setRenameStart('1');
      await loadCampaigns(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Đổi tên hàng loạt thất bại');
    } finally {
      setRenaming(false);
    }
  };

  const adAccountOptions: SelectOption[] = useMemo(
    () => adAccounts.map((a) => ({ value: a.id, label: a.name ?? adAccountNumber(a.adAccountIdFb) })),
    [adAccounts],
  );

  const columns: Column<CampaignDTO>[] = [
    {
      key: 'name',
      header: 'Tên chiến dịch',
      sortAccessor: (row) => (row.name ?? '').toLowerCase(),
      render: (row) => (
        <div>
          <div className="font-medium text-gray-900">{row.name ?? '—'}</div>
          <div className="text-xs text-gray-500">{row.objective ?? ''}</div>
        </div>
      ),
    },
    {
      key: 'account',
      header: 'Tài khoản QC / BM',
      sortAccessor: (row) => (row.adAccountName ?? row.adAccountIdFb ?? '').toLowerCase(),
      render: (row) => (
        <div>
          <div className="text-gray-900">{row.adAccountName ?? adAccountNumber(row.adAccountIdFb) ?? '—'}</div>
          <div className="text-xs text-gray-500">
            {row.adAccountIdFb ? (
              <a
                href={adsManagerUrl(row.adAccountIdFb)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 hover:underline"
                title="Mở trong Trình quản lý quảng cáo"
              >
                {adAccountNumber(row.adAccountIdFb)}
              </a>
            ) : null}
            {row.businessManagerId ? ` · BM ${row.businessManagerId}` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Trạng thái',
      sortAccessor: (row) => (row.status ?? '').toLowerCase(),
      render: (row) => {
        const meta = entityStatusMeta(row.status);
        return <Badge color={meta.color}>{meta.label}</Badge>;
      },
    },
    {
      key: 'dailyBudget',
      header: 'Ngân sách/ngày',
      align: 'right',
      sortAccessor: (row) => (row.dailyBudget != null ? Number(row.dailyBudget) : null),
      defaultSortDir: 'desc',
      render: (row) => formatCurrency(row.dailyBudget),
    },
    {
      key: 'spend',
      header: 'Đã tiêu',
      align: 'right',
      sortAccessor: (row) => (row.spend != null ? Number(row.spend) : null),
      defaultSortDir: 'desc',
      render: (row) => (row.spend != null ? formatCurrency(row.spend) : <span className="text-gray-400">—</span>),
    },
    {
      key: 'cpa',
      header: 'CPA',
      align: 'right',
      sortAccessor: (row) => (row.cpa != null ? Number(row.cpa) : null),
      defaultSortDir: 'desc',
      render: (row) => (row.cpa != null ? formatCurrency(row.cpa) : <span className="text-gray-400">—</span>),
    },
    {
      key: 'conversions',
      header: 'Chuyển đổi',
      align: 'right',
      sortAccessor: (row) => (row.conversions != null ? row.conversions : null),
      defaultSortDir: 'desc',
      render: (row) => (row.conversions != null ? formatNumber(row.conversions) : <span className="text-gray-400">—</span>),
    },
    {
      key: 'roas',
      header: 'ROAS (FB)',
      align: 'right',
      sortAccessor: (row) => (row.insightsSyncedAt != null && row.roas != null ? Number(row.roas) : null),
      defaultSortDir: 'desc',
      render: (row) => roasCell(row),
    },
    {
      key: 'revenue',
      header: 'Doanh thu',
      align: 'right',
      sortAccessor: (row) => (row.revenue != null ? Number(row.revenue) : null),
      defaultSortDir: 'desc',
      render: (row) => (row.revenue != null ? formatCurrency(row.revenue) : <span className="text-gray-400">—</span>),
    },
    {
      key: 'realRoas',
      header: 'ROAS thật',
      align: 'right',
      sortAccessor: (row) => (row.realRoas != null ? Number(row.realRoas) : null),
      defaultSortDir: 'desc',
      render: (row) => realRoasCell(row),
    },
    {
      key: 'actions',
      header: 'Hành động',
      align: 'right',
      render: (row) => {
        const isActive = (row.status ?? '').toUpperCase() === 'ACTIVE';
        return (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              loading={busyId === row.id}
              onClick={() => void handleToggleStatus(row)}
            >
              {isActive ? 'Tạm dừng' : 'Kích hoạt'}
            </Button>
            <Button variant="secondary" onClick={() => openBudget(row)}>
              Ngân sách
            </Button>
            <Button
              variant="secondary"
              loading={markingId === row.id}
              onClick={() => void handleToggleTemplate(row)}
            >
              {row.isTemplate ? '★ Bỏ mẫu' : '☆ Đánh dấu mẫu'}
            </Button>
            <Button variant="secondary" onClick={() => openCopy(row)}>
              Sao chép
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chiến dịch</h1>
          <p className="mt-1 text-sm text-gray-500">Quản lý và tối ưu các chiến dịch quảng cáo.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button loading={refreshing} onClick={() => void handleRefreshAll()}>
            🔄 Làm mới
          </Button>
        </div>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      {/* Aggregate totals for the current filter + range */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="card">
          <div className="text-xs font-medium uppercase text-gray-500">Chiến dịch (đang chạy)</div>
          <div className="mt-1 text-xl font-bold text-gray-900">
            {formatNumber(totals.running)} / {formatNumber(totals.count)}
          </div>
        </div>
        <div className="card">
          <div className="text-xs font-medium uppercase text-gray-500">Tổng đã tiêu (theo mốc)</div>
          <div className="mt-1 text-xl font-bold text-gray-900">{formatCurrency(totals.totalSpend)}</div>
        </div>
        <div className="card">
          <div className="text-xs font-medium uppercase text-gray-500">Tổng doanh thu (theo mốc)</div>
          <div className="mt-1 text-xl font-bold text-gray-900">{formatCurrency(totals.totalRevenue)}</div>
        </div>
        <div className="card">
          <div className="text-xs font-medium uppercase text-gray-500">ROAS tổng (thật)</div>
          <div
            className={`mt-1 text-xl font-bold ${
              totals.totalSpend > 0 && totals.totalRevenue / totals.totalSpend >= 1
                ? 'text-green-600'
                : 'text-gray-900'
            }`}
          >
            {totals.totalSpend > 0
              ? `${(totals.totalRevenue / totals.totalSpend).toFixed(2)}x`
              : '—'}
          </div>
        </div>
      </div>

      <Card>
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <MultiSelect
            label="Tài khoản quảng cáo"
            options={adAccountOptions}
            allLabel="Tất cả tài khoản"
            value={accountIds}
            onChange={setAccountIds}
          />
          <Select
            label="Trạng thái"
            options={STATUS_OPTIONS}
            placeholder="Tất cả trạng thái"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          />
          <Select
            label="Khoảng thời gian (chi tiêu)"
            options={RANGE_OPTIONS}
            value={rangePreset}
            onChange={(e) => setRangePreset(e.target.value as RangePreset)}
          />
        </div>
        {rangePreset === 'custom' && (
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="Từ ngày"
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
            />
            <Input
              label="Đến ngày"
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
            />
          </div>
        )}

        {selectedIds.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-brand-200 bg-brand-50 p-3">
            <span className="text-sm font-semibold text-brand-800">{selectedIds.length} đã chọn</span>
            <Button variant="secondary" loading={bulking} onClick={() => void handleBulkStatus('ACTIVE')}>
              Kích hoạt
            </Button>
            <Button variant="secondary" loading={bulking} onClick={() => void handleBulkStatus('PAUSED')}>
              Tạm dừng
            </Button>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0}
                value={bulkBudgetValue}
                onChange={(e) => setBulkBudgetValue(e.target.value)}
                placeholder="Ngân sách/ngày"
                className="input w-36"
              />
              <Button variant="secondary" loading={bulking} onClick={() => void handleBulkBudget()}>
                Cập nhật NS
              </Button>
            </div>
            <Button variant="secondary" onClick={() => setRenameOpen(true)}>
              Đổi tên
            </Button>
            <button
              type="button"
              className="ml-auto text-sm text-gray-500 underline"
              onClick={() => setSelectedIds([])}
            >
              Bỏ chọn
            </button>
          </div>
        )}

        <Table
          columns={columns}
          rows={campaigns}
          loading={loading}
          rowKey={(row) => row.id}
          selectable
          selected={selectedIds}
          onSelectedChange={setSelectedIds}
          emptyMessage="Chưa có chiến dịch. Chọn tài khoản quảng cáo và nhấn Đồng bộ."
        />
      </Card>

      {/* Copy modal */}
      <Modal
        isOpen={copyTarget !== null}
        onClose={() => setCopyTarget(null)}
        title="Sao chép chiến dịch"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCopyTarget(null)}>
              Hủy
            </Button>
            <Button type="submit" form="copy-campaign-form" loading={copying}>
              Sao chép
            </Button>
          </>
        }
      >
        <form id="copy-campaign-form" onSubmit={handleCopy} className="space-y-4">
          <Input
            label="Tên chiến dịch mới"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Để trống để dùng tên mặc định"
          />
        </form>
      </Modal>

      {/* Budget modal */}
      <Modal
        isOpen={budgetTarget !== null}
        onClose={() => setBudgetTarget(null)}
        title="Chỉnh sửa ngân sách"
        footer={
          <>
            <Button variant="secondary" onClick={() => setBudgetTarget(null)}>
              Hủy
            </Button>
            <Button type="submit" form="budget-form" loading={savingBudget}>
              Lưu
            </Button>
          </>
        }
      >
        <form id="budget-form" onSubmit={handleSaveBudget} className="space-y-4">
          <Input
            label="Ngân sách hằng ngày (VND)"
            type="number"
            min={0}
            value={dailyBudget}
            onChange={(e) => setDailyBudget(e.target.value)}
            placeholder="Ví dụ: 500000"
          />
          <Input
            label="Ngân sách trọn đời (VND)"
            type="number"
            min={0}
            value={lifetimeBudget}
            onChange={(e) => setLifetimeBudget(e.target.value)}
            placeholder="Ví dụ: 5000000"
          />
          <p className="text-xs text-gray-500">Chỉ cần nhập loại ngân sách bạn muốn thay đổi.</p>
        </form>
      </Modal>

      {/* Bulk rename modal */}
      <Modal
        isOpen={renameOpen}
        onClose={() => setRenameOpen(false)}
        title={`Đổi tên ${selectedIds.length} chiến dịch`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRenameOpen(false)}>
              Hủy
            </Button>
            <Button type="submit" form="bulk-rename-form" loading={renaming}>
              Áp dụng
            </Button>
          </>
        }
      >
        <form id="bulk-rename-form" onSubmit={handleBulkRename} className="space-y-4">
          <Input
            label="Tên mới"
            value={renameBase}
            onChange={(e) => setRenameBase(e.target.value)}
            placeholder="vd: Combo Week360 + Finance360"
            required
          />
          <Input
            label="Hậu tố (tuỳ chọn)"
            value={renameSuffix}
            onChange={(e) => setRenameSuffix(e.target.value)}
            placeholder="vd: camp  (hoặc: set ngày 25/7)"
          />
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                checked={renameNumber}
                onChange={(e) => setRenameNumber(e.target.checked)}
              />
              Đánh số thứ tự tăng dần
            </label>
            {renameNumber && (
              <Input
                label=""
                type="number"
                min={0}
                value={renameStart}
                onChange={(e) => setRenameStart(e.target.value)}
                wrapperClassName="w-28"
                placeholder="bắt đầu từ"
              />
            )}
          </div>
          <div className="rounded-md bg-gray-50 p-3 text-xs">
            <div className="text-gray-500">Xem trước ({selectedIds.length} camp):</div>
            <div className="mt-1 font-medium text-gray-900">
              {buildRenamed(0) || '(nhập tên mới)'}
              {selectedIds.length > 1 && renameNumber ? ` · ${buildRenamed(1)} · …` : ''}
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Đặt tên mới cho tất cả camp đã chọn (không quan tâm tên cũ). Bật STT để tự đánh số 1, 2, 3… Camp
            đặc biệt (boosted post…) có thể bị Facebook chặn đổi tên.
          </p>
        </form>
      </Modal>
    </div>
  );
}
